"use client";

import type { ReactElement } from "react";
import React from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { Card } from "@jetblue/kit/card";
import { Button } from "@jetblue/kit/button";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";

import type { BuiltFlightCard } from "./features/build-flight-cards";
import { CopyIcon } from "./features/build-flight-cards";
import { useManageTripsConfig } from "@jb-manage-trips/runtime-config";
import type { CardButtonMeta } from "@jb-manage-trips/types/itinerary";
import type { ManageTripsRuntimeConfig } from "@jb-manage-trips/types/config";

type Properties = {
  readonly className?: string;
  readonly title?: string;
  readonly flightCards?: readonly BuiltFlightCard[];
  readonly buttonMeta?: readonly CardButtonMeta[];
  readonly onManageTripClick?: (
    index: number,
    isSelfServiceButton: boolean,
  ) => void;
};

type TripCardGroup = {
  tripKey: string;
  tripIndex: number;
  cards: BuiltFlightCard[];
};

function groupCardsByTrip(cards: readonly BuiltFlightCard[]): TripCardGroup[] {
  const map = new Map<string, TripCardGroup>();

  for (const card of cards) {
    const existing = map.get(card.tripKey);

    if (existing) {
      existing.cards.push(card);
      continue;
    }

    map.set(card.tripKey, {
      tripKey: card.tripKey,
      tripIndex: card.tripIndex,
      cards: [card],
    });
  }

  return [...map.values()];
}

function DefaultFlightCard(): BuiltFlightCard {
  return {
    key: "default-card",
    tripKey: "default-trip",
    tripIndex: 0,
    legIndex: 0,
    legCount: 1,
    confirmationCode: "ABC123",
    destinationLabel: "Destination placeholder",
    tripMetaLabel: "Upcoming trip • One-way",
    statusLabel: "Check-in is open",
    imageUrl: "",
    flightDetailCard: (
      <Card
        as="div"
        className="w-full rounded-2xl border border-light-gray bg-white p-5"
      >
        <div className="h-48 animate-pulse rounded-xl bg-light-gray" />
      </Card>
    ),
  };
}

function normalizeFlightDetailCard(
  flightDetailCard: ReactElement,
): ReactElement {
  if (!React.isValidElement<{ className?: string }>(flightDetailCard)) {
    return flightDetailCard;
  }

  return React.cloneElement(flightDetailCard, {
    className: twMerge(
      flightDetailCard.props.className,
      "w-full overflow-hidden! p-0!",
    ),
  });
}

function DestinationImage({
  imageUrl,
  destinationLabel,
}: {
  readonly imageUrl?: string;
  readonly destinationLabel: string;
}): ReactElement {
  if (!imageUrl) {
    return (
      <div className="flex h-[88px] w-[88px] items-center justify-center rounded-xl border border-medium-gray bg-off-white text-center text-legal text-slate-gray md:h-[96px] md:w-[96px] lg:h-[144px] lg:w-[144px]">
        Trip image
      </div>
    );
  }

  return (
    <div className="h-[88px] w-[88px] overflow-hidden rounded-xl border border-medium-gray bg-off-white md:h-[96px] md:w-[96px] lg:h-[144px] lg:w-[144px]">
      <img
        alt={destinationLabel}
        className="size-full object-cover"
        src={imageUrl}
      />
    </div>
  );
}

type ActionConfig = {
  actionLabel: string;
  actionAriaLabel: string | undefined;
  isSelfServiceButton: boolean;
  isDisabled: boolean;
};

function getActionPresentation(meta?: CardButtonMeta): ActionConfig[] {
  const actions: ActionConfig[] = [];
  actions.push({
    actionLabel: meta?.normalLabel ?? "Manage trip",
    actionAriaLabel: meta?.ariaNormal,
    isSelfServiceButton: false,
    isDisabled: false,
  });

  if (meta?.pnrState !== "normal") {
    actions.push({
      actionLabel: meta?.disruptedLabel ?? "View travel options",
      actionAriaLabel: meta?.ariaDisrupted,
      isSelfServiceButton: true,
      isDisabled: !meta?.hasSelfService,
    });
  }

  return actions;
}

type CtaRowProperties = {
  readonly tripIndex: number;
  readonly meta?: CardButtonMeta;
  readonly onManageTripClick?: (
    index: number,
    isSelfServiceButton: boolean,
  ) => void;
  readonly isCheckInEnabled: boolean;
};

function CtaRow({
  tripIndex,
  meta,
  onManageTripClick,
  isCheckInEnabled,
}: CtaRowProperties): ReactElement {
  const actions = getActionPresentation(meta);

  return (
    <div className="mt-5 grid gap-3 md:w-full md:grid-cols-2 lg:mt-8 lg:gap-4">
      {isCheckInEnabled && (
        <Button
          aria-label="Check in"
          className="w-full justify-center rounded-full bg-royal-blue font-sans text-body-extra-bold text-white md:min-h-[72px]"
          size="large"
          type="button"
        >
          <span className="text-white">Check in</span>
        </Button>
      )}

      {actions.map(
        (
          { actionLabel, actionAriaLabel, isSelfServiceButton, isDisabled },
          index,
        ) => (
          <Button
            aria-label={actionAriaLabel}
            className="w-full justify-center rounded-full border border-dark-gray bg-white font-sans text-body-extra-bold text-royal-blue md:min-h-[72px]"
            disabled={isDisabled}
            key={index}
            onClick={() => onManageTripClick?.(tripIndex, isSelfServiceButton)}
            size="large"
            type="button"
            variant="secondary"
          >
            <span>{actionLabel}</span>
          </Button>
        ),
      )}
    </div>
  );
}

type InnerCarouselProperties = {
  readonly items: readonly BuiltFlightCard[];
  readonly onSelect: (index: number) => void;
};

function InnerCarousel({
  items,
  onSelect,
}: InnerCarouselProperties): ReactElement {
  const autoplay = React.useRef(
    Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true }),
  );

  const [emblaReference, emblaApi] = useEmblaCarousel(
    { loop: false, align: "start", slidesToScroll: 1 },
    [autoplay.current],
  );

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [canScrollPrevious, setCanScrollPrevious] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) {
      return;
    }

    const updateState = () => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      setCanScrollPrevious(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
      onSelect(index);
    };

    updateState();
    emblaApi.on("select", updateState);

    return () => {
      emblaApi.off("select", updateState);
    };
  }, [emblaApi, onSelect]);

  return (
    <div>
      <div className="grid items-center gap-2 md:grid-cols-[40px_minmax(0,1fr)_40px] md:gap-2 lg:grid-cols-[48px_minmax(0,1fr)_48px] lg:gap-3">
        <div className="hidden md:flex md:justify-center">
          <button
            aria-label="Previous flight"
            className={clsx(
              "flex size-9 items-center justify-center rounded-full bg-royal-blue text-white transition hover:bg-core-blue active:scale-95 lg:size-10",
              !canScrollPrevious && "cursor-not-allowed opacity-40",
            )}
            disabled={!canScrollPrevious}
            onClick={() => emblaApi?.scrollPrev()}
            type="button"
          >
            ←
          </button>
        </div>

        <div className="min-w-0 overflow-hidden" ref={emblaReference}>
          <div className="flex touch-pan-x">
            {items.map((card, index) => (
              <div className="min-w-0 flex-[0_0_100%]" key={card.key || index}>
                <div className="w-full">
                  {normalizeFlightDetailCard(card.flightDetailCard)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden md:flex md:justify-center">
          <button
            aria-label="Next flight"
            className={clsx(
              "flex size-9 items-center justify-center rounded-full bg-royal-blue text-white transition hover:bg-core-blue active:scale-95 lg:size-10",
              !canScrollNext && "cursor-not-allowed opacity-40",
            )}
            disabled={!canScrollNext}
            onClick={() => emblaApi?.scrollNext()}
            type="button"
          >
            →
          </button>
        </div>
      </div>

      {items.length > 1 && (
        <div className="mt-4 flex justify-center gap-2">
          {items.map((_, index) => (
            <button
              aria-label={`Go to flight ${String(index + 1)}`}
              className={clsx(
                "size-2 rounded-full border border-royal-blue",
                selectedIndex === index ? "bg-royal-blue" : "bg-transparent",
              )}
              key={index}
              onClick={() => emblaApi?.scrollTo(index)}
              type="button"
            />
          ))}
        </div>
      )}
    </div>
  );
}

type TripGroupShellProperties = {
  readonly group: TripCardGroup;
  readonly buttonMeta?: readonly CardButtonMeta[];
  readonly onManageTripClick?: (
    index: number,
    isSelfServiceButton: boolean,
  ) => void;
  readonly isCheckInEnabled: boolean;
};

function TripGroupShell({
  group,
  buttonMeta,
  onManageTripClick,
  isCheckInEnabled,
}: TripGroupShellProperties): ReactElement {
  const [selectedIndex, setSelectedIndex] = React.useState(0);

  const safeIndex =
    selectedIndex >= 0 && selectedIndex < group.cards.length
      ? selectedIndex
      : 0;

  const shellCard = group.cards[0] ?? DefaultFlightCard();
  const activeCard = group.cards[safeIndex] ?? shellCard;
  const activeMeta = buttonMeta?.[group.tripIndex];

  return (
    <Card
      as="div"
      className="w-full overflow-hidden rounded-3xl border border-light-gray bg-white"
    >
      <div className="bg-royal-blue py-1 text-center text-xs-heading-extra-bold text-white md:w-fit md:rounded-br-2xl md:px-6 md:text-xs-heading">
        {activeCard.statusLabel}
      </div>

      <div className="p-4 md:px-6 md:pb-5 lg:px-8 lg:pt-7 lg:pb-8">
        <div className="grid gap-6 lg:min-h-[600px] lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-8">
          <div className="flex items-start gap-4 lg:flex-col lg:gap-5 lg:pt-2">
            <DestinationImage
              destinationLabel={shellCard.destinationLabel}
              imageUrl={shellCard.imageUrl}
            />

            <div className="min-w-0 text-core-blue">
              <div className="text-m-heading leading-[1.02] md:text-l-heading">
                {shellCard.destinationLabel}
              </div>

              <div className="mt-1 text-xs-heading lg:mt-3 lg:leading-tight">
                {shellCard.tripMetaLabel}
              </div>

              <div className="mt-2 flex items-center gap-2 text-xs-heading-extra-bold text-royal-blue lg:mt-4">
                <span>{shellCard.confirmationCode}</span>
                <CopyIcon />
              </div>
            </div>
          </div>

          <div className="min-w-0">
            <InnerCarousel items={group.cards} onSelect={setSelectedIndex} />

            <CtaRow
              isCheckInEnabled={isCheckInEnabled}
              meta={activeMeta}
              onManageTripClick={onManageTripClick}
              tripIndex={group.tripIndex}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}

export const UpcomingTrips = ({
  className = "",
  title = "",
  flightCards,
  buttonMeta,
  onManageTripClick,
}: Properties): ReactElement | null => {
  if (!title) {
    return null;
  }

  const config: ManageTripsRuntimeConfig = useManageTripsConfig();
  const isCheckInEnabled: boolean =
    config.MANAGE_TRIPS.FLAGS.IS_CHECK_IN_ENABLED;

  const cards: readonly BuiltFlightCard[] =
    Array.isArray(flightCards) && flightCards.length > 0
      ? flightCards
      : [DefaultFlightCard()];

  const groupedCards = groupCardsByTrip(cards);

  return (
    <section
      className={twMerge(
        clsx(
          "flex justify-center bg-off-white px-4 py-6 md:px-9 lg:px-6",
          className,
        ),
      )}
    >
      <div className="w-full max-w-[1024px] min-w-[320px]">
        <div className="mb-4 md:mb-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="font-sans text-l-heading text-core-blue lg:leading-none">
              {title}
            </h1>

            <div className="flex items-center gap-6 font-sans text-s-heading text-core-blue md:text-s-heading lg:gap-8 lg:text-l-heading">
              <button
                aria-current="page"
                className="border-b-2 border-core-blue pb-1 text-s-heading-extra-bold text-core-blue md:text-s-heading-extra-bold"
                type="button"
              >
                Upcoming
              </button>
              <button
                className="pb-1 text-s-heading text-slate-gray hover:text-core-blue"
                type="button"
              >
                Past
              </button>
            </div>
          </div>
        </div>

        <div
          className="flex flex-col gap-6"
          data-testid="upcoming-trips-shells"
        >
          {groupedCards.map((group) => (
            <TripGroupShell
              buttonMeta={buttonMeta}
              group={group}
              isCheckInEnabled={isCheckInEnabled}
              key={group.tripKey}
              onManageTripClick={onManageTripClick}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
